import { Experience } from '../../../types/experience';
import { getDetailsSchema, getDetailsSchemaPartial } from './Details/schema';
import {
    LocationSchema,
    LocationSchemaPartial,
    VehicleLocationSchema,
    VehicleLocationSchemaPartial,
} from './Location/schema';
import { AvailabilityMode, ExperienceType } from '../../../enums/experience';
import {
    getAmenitiesPartialSchema,
    getAmenitiesSchema,
} from './Amenities/schema';
import { getAvailabilitySchema } from './Availability/schema';
import { getExperiencePricingSchema } from './Pricing/schema';
import { PhotosPartialSchema, PhotosSchema } from './Photos/schema';
import { VideosSchema } from './Videos/schema';
import { FloorPlansSchema } from './FloorPlans/schema';
import { z } from 'zod';
import { DefaultValues } from 'react-hook-form';
import { Step } from './useFormState';
import { getGuidesSchema } from './Guides/schema';
import { DiscountsSchema } from './Discounts/schema';
import { PoliciesSchema } from './Policies/schema';
import { AccessSchema } from './Access/schema';
import { TrackingSchema } from './Tracking/schema';
import { IntegrationsSchema } from './Integrations/schema';

export const getStepSchema = (
    step: Step,
    experience: Experience,
    allAssets: Experience[],
): {
    schema: z.ZodSchema<any>;
    partialSchema?: z.ZodSchema<any>;
    defaultValues: DefaultValues<Partial<Experience>>;
} => {
    switch (step) {
        case 'location':
            return {
                schema:
                    experience.type === ExperienceType.Vehicle
                        ? VehicleLocationSchema
                        : LocationSchema,
                partialSchema:
                    experience.type === ExperienceType.Vehicle
                        ? VehicleLocationSchemaPartial
                        : LocationSchemaPartial,
                defaultValues: {
                    streetAddress: '',
                    suburb: '',
                    state: undefined,
                    postcode: '',
                    countryCode: 'AU',
                    phone: '',
                    usesNirovision: false,
                    nirovisionLocationId: '',
                    nirovisionLabelId: '',
                    nirovisionVehicleLabelId: '',
                    ...(experience.type === ExperienceType.Vehicle
                        ? {
                              details: {
                                  dropoffLocationSameAsPickup: true,
                                  dropoffStreetAddress: '',
                                  dropoffSuburb: '',
                                  dropoffState: undefined,
                                  dropoffPostcode: '',
                                  dropoffCountryCode: 'AU',
                                  dropoffPhone: '',
                                  dropoffUsesNirovision: false,
                                  dropoffNirovisionLocationId: '',
                                  dropoffNirovisionLabelId: '',
                                  dropoffNirovisionVehicleLabelId: '',
                              },
                          }
                        : undefined),
                    addressVisible: true,
                },
            };
        case 'amenities':
            return {
                schema: getAmenitiesSchema(experience),
                partialSchema: getAmenitiesPartialSchema(experience),
                defaultValues:
                    experience.type === ExperienceType.Activity
                        ? {
                              details: {
                                  quantity: 0,
                              },
                          }
                        : experience.type === ExperienceType.Boat
                          ? {
                                details: {
                                    bedrooms: 0,
                                    beds: 0,
                                    baths: 0,
                                    maxGuests: 0,
                                    overnightGuests: 0,
                                    catering: false,
                                    alcohol: false,
                                    wifi: false,
                                    boardroom: false,
                                    tv: false,
                                    kitchen: false,
                                    aircon: false,
                                    bbq: false,
                                    spa: false,
                                    waterSports: false,
                                },
                                spaces: [],
                            }
                          : experience.type === ExperienceType.Commercial
                            ? {
                                  details: {
                                      desks: 0,
                                      privateOffices: 0,
                                      meetingRooms: 0,
                                      maxGuests: 0,
                                      meetingRoomMaxGuests: 0,
                                      catering: false,
                                      wifi: false,
                                      boardroom: false,
                                      tv: false,
                                      kitchen: false,
                                      parking: false,
                                      aircon: false,
                                      dedicatedWorkspace: false,
                                      balcony: false,
                                      bbq: false,
                                      gym: false,
                                      diningOptions: false,
                                      petFriendly: false,
                                      showerRoom: false,
                                      bikeLock: false,
                                      personalStorage: false,
                                      childcare: false,
                                  },
                                  spaces: [],
                              }
                            : experience.type === ExperienceType.Property
                              ? {
                                    details: {
                                        bedrooms: 0,
                                        beds: 0,
                                        baths: 0,
                                        maxGuests: 0,
                                        overnightGuests: 0,
                                        catering: false,
                                        alcohol: false,
                                        wifi: false,
                                        boardroom: false,
                                        tv: false,
                                        kitchen: false,
                                        parking: false,
                                        aircon: false,
                                        dedicatedWorkspace: false,
                                        pool: false,
                                        bbq: false,
                                        firepit: false,
                                        washingMachine: false,
                                        gamesRoom: false,
                                        spa: false,
                                        wineCellar: false,
                                        massages: false,
                                    },
                                    spaces: [],
                                }
                              : experience.details.subType === 'car'
                                ? {
                                      details: {
                                          make: '',
                                          model: '',
                                          year: null,
                                          heatedSeats: false,
                                          appleCarPlay: false,
                                          androidAuto: false,
                                          rearVisionCamera: false,
                                          cruiseControl: false,
                                          touchscreenDisplay: false,
                                          bluetooth: false,
                                          blindSpotAlert: false,
                                          navigation: false,
                                          tyrePressureSensor: false,
                                          airbags: false,
                                          ac: false,
                                          keylessEntry: false,
                                          sunroof: false,
                                          laneAssist: false,
                                          autoPark: false,
                                          powerSeats: false,
                                          remoteStart: false,
                                          wirelessPhoneCharger: false,
                                          rearEntertainmentSystem: false,
                                          bodyType: '',
                                      },
                                  }
                                : {
                                      details: {
                                          make: '',
                                          model: '',
                                          year: null,
                                          heatedSeats: false,
                                          appleCarPlay: false,
                                          androidAuto: false,
                                          rearVisionCamera: false,
                                          cruiseControl: false,
                                          touchscreenDisplay: false,
                                          bluetooth: false,
                                          blindSpotAlert: false,
                                          navigation: false,
                                          tyrePressureSensor: false,
                                          airbags: false,
                                          helmet: false,
                                          gloves: false,
                                          adjustableSeats: false,
                                      },
                                  },
            };
        case 'availability':
            return {
                schema: getAvailabilitySchema(experience.type),
                defaultValues: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false,
                    publicHolidays: false,
                    requiresBookingApproval: true,
                    autoApprovalGuests: [],
                    ...(experience.type === ExperienceType.Vehicle
                        ? {
                              details: {
                                  minimumLicenceRequired: 'full',
                                  petsAllowed: false,
                                  smokingAllowed: false,
                              },
                          }
                        : {}),
                },
            };
        case 'photos':
            return {
                schema: PhotosSchema,
                partialSchema: PhotosPartialSchema,
                defaultValues: {
                    photos: [],
                },
            };
        case 'videos':
            return {
                schema: VideosSchema,
                defaultValues: {
                    videos: [],
                },
            };
        case 'pricing': {
            return {
                schema: getExperiencePricingSchema(experience, allAssets),
                defaultValues: {
                    charges: [],
                },
            };
        }
        case 'floorplans':
            return {
                schema: FloorPlansSchema,
                defaultValues: {
                    floorPlans: [],
                },
            };
        case 'tracking': {
            return {
                schema: TrackingSchema,
                defaultValues: {
                    device: null,
                },
            };
        }
        case 'guides':
            return {
                schema: getGuidesSchema(experience.type),
                defaultValues: {
                    checkinDetails: '',
                    ...(experience.type !== ExperienceType.Activity
                        ? {
                              details: {
                                  ...(experience.type === ExperienceType.Vehicle
                                      ? { rules: '' }
                                      : { wifiDetails: '' }),
                              },
                          }
                        : {}),
                    guides: [],
                },
            };
        case 'discounts':
            return {
                schema: DiscountsSchema,
                defaultValues: {
                    discounts: [],
                },
            };
        case 'policies':
            return {
                schema: PoliciesSchema,
                defaultValues: {
                    policy: null,
                },
            };
        case 'access': {
            return {
                schema: AccessSchema,
                defaultValues: {
                    guests: [],
                },
            };
        }
        case 'integrations': {
            return {
                schema: IntegrationsSchema,
                defaultValues: {
                    linkedModules: [],
                    linkType: 'block', // do we need this?
                    availabilityMode: AvailabilityMode.Standalone,
                    addOnOf: [],
                    contractors: [],
                },
            };
        }
        default:
            return {
                schema: getDetailsSchema(experience, allAssets),
                partialSchema: getDetailsSchemaPartial(experience, allAssets),
                defaultValues: {
                    name: '',
                    description: '',
                    visitType: 'business',
                    notificationsEmail: '',
                },
            };
    }
};
